"use client";

import React from "react";
import { getApiBaseUrl } from "@/lib/api";
import { setToken } from "@/lib/auth";
import { useTranslations } from "@/hooks/useTranslations";

const GSI_SCRIPT = "https://accounts.google.com/gsi/client";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (res: { credential: string }) => void;
            auto_select?: boolean;
          }) => void;
          renderButton: (
            el: HTMLElement,
            config: { theme?: "outline" | "filled_blue" | "filled_black"; size?: "large" | "medium" | "small"; type?: "standard" | "icon"; width?: number }
          ) => void;
        };
      };
    };
  }
}

let gsiScriptPromise: Promise<void> | null = null;
let gsiInitializedClientId: string | null = null;
let gsiCredentialHandler: ((credential: string) => void) | null = null;

function loadScript(src: string): Promise<void> {
  if (gsiScriptPromise) return gsiScriptPromise;

  gsiScriptPromise = new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gsiScriptPromise = null;
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });

  return gsiScriptPromise;
}

function ensureGsiInitialized(clientId: string): Promise<void> {
  return loadScript(GSI_SCRIPT).then(() => {
    if (!window.google?.accounts?.id) {
      throw new Error("Google Identity Services unavailable");
    }
    if (gsiInitializedClientId === clientId) return;

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (res) => gsiCredentialHandler?.(res.credential),
    });
    gsiInitializedClientId = clientId;
  });
}

function renderGoogleButton(
  el: HTMLElement,
  config: { theme: "outline" | "filled_blue" | "filled_black"; size: "large" | "medium" | "small" }
) {
  el.replaceChildren();
  const width = el.offsetWidth || el.parentElement?.clientWidth || 320;
  window.google!.accounts.id.renderButton(el, {
    theme: config.theme,
    size: config.size,
    type: "standard",
    width,
  });
}

export type GoogleSignInButtonProps = {
  onSuccess?: () => void;
  disabled?: boolean;
  theme?: "outline" | "filled_blue" | "filled_black";
  size?: "large" | "medium" | "small";
  className?: string;
};

export function GoogleSignInButton({
  onSuccess,
  disabled = false,
  theme = "outline",
  size = "large",
  className,
}: GoogleSignInButtonProps) {
  const { t } = useTranslations();
  const googleFailed = t("authFlow.googleFailed");
  const googleNotConfigured = t("authFlow.googleNotConfigured");
  const googleCouldNotLoad = t("authFlow.googleCouldNotLoad");
  const containerRef = React.useRef<HTMLDivElement>(null);
  const onSuccessRef = React.useRef(onSuccess);
  const handleCredentialRef = React.useRef<(credential: string) => Promise<void>>(async () => {});
  const [mounted, setMounted] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const clientId = mounted && typeof window !== "undefined" ? (process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? "").trim() : "";

  onSuccessRef.current = onSuccess;

  handleCredentialRef.current = async (credential: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${getApiBaseUrl()}/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken: credential }),
      });
      if (!response.ok) {
        const data = (await response.json()) as { message?: string };
        throw new Error(data.message ?? googleFailed);
      }
      const data = (await response.json()) as { accessToken: string };
      setToken(data.accessToken);
      onSuccessRef.current?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : googleFailed);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    gsiCredentialHandler = (credential) => {
      void handleCredentialRef.current(credential);
    };
  });

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (!mounted || !clientId) {
      if (mounted && !clientId) setError(googleNotConfigured);
      return;
    }

    setError(null);
    let cancelled = false;

    ensureGsiInitialized(clientId)
      .then(() => {
        if (cancelled) return;
        const el = containerRef.current;
        if (!el || !window.google?.accounts?.id) return;
        renderGoogleButton(el, { theme, size });
      })
      .catch(() => {
        if (!cancelled) setError(googleCouldNotLoad);
      });

    return () => {
      cancelled = true;
      containerRef.current?.replaceChildren();
    };
  }, [mounted, clientId, theme, size, googleNotConfigured, googleCouldNotLoad]);

  React.useEffect(() => {
    if (!mounted || !clientId) return;

    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      if (!window.google?.accounts?.id || !containerRef.current) return;
      renderGoogleButton(containerRef.current, { theme, size });
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [mounted, clientId, theme, size]);

  const buttonHeight = size === "large" ? "h-11" : size === "medium" ? "h-9" : "h-8";
  const inactive = disabled || loading;

  if (!mounted) {
    return <div className={`${className} ${buttonHeight} w-full`} aria-hidden />;
  }

  return (
    <div className={className}>
      {clientId ? (
        <div className={`group relative w-full overflow-hidden rounded-full ${buttonHeight}`}>
          {/* Visible style matching site: rounded-full, border, bg like form inputs */}
          <div
            className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 text-sm font-semibold text-slate-100 transition-colors group-hover:border-white/20 group-hover:bg-white/10 [&>svg]:h-5 [&>svg]:w-5"
            aria-hidden
          >
            <GoogleIcon />
            <span>{t("authFlow.googleSignIn")}</span>
          </div>
          {/* Nearly invisible Google iframe on top receives clicks (opacity 0 blocks pointer events in some browsers). */}
          <div
            ref={containerRef}
            aria-label={t("authFlow.googleSignIn")}
            className="absolute inset-0 z-10 overflow-hidden [&>div]:!mx-0 [&>div]:!h-full [&>div]:!w-full [&>iframe]:!h-full [&>iframe]:!w-full [&>iframe]:!min-h-full"
            style={{
              opacity: 0.01,
              cursor: inactive ? "not-allowed" : "pointer",
              pointerEvents: inactive ? "none" : "auto",
            }}
          />
        </div>
      ) : (
        <p className="py-2 text-center text-sm text-amber-500/90">
          {googleNotConfigured}
        </p>
      )}
      {loading && (
        <p className="mt-2 text-center text-sm text-slate-400">{t("login.signingIn")}</p>
      )}
      {error && clientId && <p className="mt-2 text-sm text-rose-400">{error}</p>}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
